import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { type Resolver, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useFormError } from '@/hooks/use-form-error';
import type {
  CreateConfigProfileDto,
  UpdateConfigProfileDto,
} from '../../api/production/configuration-profiles';
import { configurationProfileApi } from '../../api/production/configuration-profiles';
import { formatConfigurationProfileName } from '../../lib/constants/configuration-profile';
import type {
  ConfigurationProfileSchema,
  TechnologySystemName,
} from '../../schemas/production/configuration-profile.schema';
import { configProfileSchema } from '../../schemas/production/configuration-profile.schema';
import type { IConfigurationProfile } from '../../types/production/configuration-profile';
import { DeviceModel } from '../../types/production/configuration-profile';
import {
  getConfigurationProfileStatus,
  isProfileActive,
} from '../../types/production/configuration-profile-list.type';
import { useToast } from '../use-toast';

export interface ITechnology {
  id: string;
  name: {
    brand: string;
    system: DeviceModel;
  };
}

export interface IClient {
  id: string;
  trade_name?: string;
  company_name?: string;
  document?: {
    value: string;
  };
}

export interface ConfigProfileFormDefaultValues {
  configurationProfile?: IConfigurationProfile;
  technology?: ITechnology;
  client?: IClient;
}

type ProfileName = {
  type?: string;
  technology?: string;
  document?: string;
};

export interface Props {
  technologies: ITechnology[];
  defaultValues?: ConfigProfileFormDefaultValues;
  isEdit?: boolean;
  isCopy?: boolean;
}

export function useConfigurationProfileForm(props: Props) {
  const { formError, showFormError, clearFormError } = useFormError();
  const { technologies, defaultValues, isEdit, isCopy } = props;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [name, setName] = useState<ProfileName>({});

  const form = useForm<ConfigurationProfileSchema>({
    resolver: zodResolver(configProfileSchema) as Resolver<ConfigurationProfileSchema>,
    defaultValues: {
      config: {
        general: {
          data_transmission_on: 60,
          data_transmission_off: 7200,
          keep_alive: 60,
        },
      },
      manual_client_register: false,
    },
  });

  const profile = defaultValues?.configurationProfile;
  const status = profile ? getConfigurationProfileStatus(profile) : undefined;
  const isActive = profile ? isProfileActive(profile) : false;
  const isInactive = status === 'INATIVO';
  const isAwaitingValidation = status === 'AGUARDANDO_VALIDACAO';
  // TODO: restore lock when validation flow is enforced again
  // const isLocked = !!isEdit && (isActive || isInactive);
  const isLocked = false;

  const createMutation = useMutation({
    mutationFn: (data: CreateConfigProfileDto) => configurationProfileApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config-profiles'] });
      toast({
        title: 'Perfil Criado!',
        description: 'Novo perfil criado com sucesso.',
        variant: 'success',
      });
      navigate('/production/config-profiles');
    },
    onError: (error) => {
      console.error('Erro ao criar perfil:', error);
      showFormError(error);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateConfigProfileDto }) =>
      configurationProfileApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config-profiles'] });
      toast({
        title: 'Perfil Atualizado!',
        description: 'As configurações foram atualizadas com sucesso.',
        variant: 'success',
      });
      navigate('/production/config-profiles');
    },
    onError: (error) => {
      console.error('Erro ao atualizar perfil:', error);
      showFormError(error);
    },
  });

  const handleSubmit = form.handleSubmit(
    async (data) => {
      if (isLocked) return;

      const { id } = data;

      const { technology_system_name: _tsn, ...specificFields } = data.config.specific ?? {};

      const config = {
        general: { ...data.config.general },
        specific: specificFields,
      };

      // Respeita o nome digitado pelo usuário no campo "Nome do Perfil".
      // O nome auto-gerado (tecnologia.tipo.documento) serve apenas como
      // sugestão inicial via form.setValue; o valor final é o do campo.
      const profile: CreateConfigProfileDto = {
        name: data.name?.trim() || formatConfigurationProfileName(name),
        type: data.type,
        technology_id: data.technology_id,
        technology_system_name: data.technology_system_name,
        client_id: data.client_id || undefined,
        manual_client: data.manual_client,
        manual_client_register: data.manual_client_register,
        config,
      };

      try {
        if (id) {
          await updateMutation.mutateAsync({ id, data: profile });
        } else {
          await createMutation.mutateAsync(profile);
        }
      } catch (_error) {
        // Error handling is done in mutation callbacks
      }
    },
    (error) => {
      console.error('Erro de validação ao registrar perfil:', error);
    },
  );

  const handleChangeName = useCallback(
    (props: ProfileName) => {
      setName((prev) => {
        const state = { ...prev, ...props };
        form.setValue('name', formatConfigurationProfileName(state));
        return state;
      });
    },
    [form],
  );

  const handleChangeTechnology = (technology_id: string) => {
    const selected = technologies.find((t) => t.id === technology_id);
    if (selected) {
      handleChangeName({
        technology: selected.name.brand,
      });

      switch (selected.name.system) {
        case DeviceModel.DM_BWS_LORA:
          form.unregister(`config.general`);
          form.unregister(`config.specific`);
          break;
        case DeviceModel.DM_BWS_NB2_LORA:
          // NB-2 LoRa precisa de APN (config.general.apn), assim como o NB-2.
          // Mantém config.general registrado e limpa apenas os campos gerais
          // que não se aplicam, preservando o sub-objeto apn.
          form.unregister(`config.general.ip_primary`);
          form.unregister(`config.general.ip_secondary`);
          form.unregister(`config.general.dns_primary`);
          form.unregister(`config.general.dns_secondary`);
          form.unregister(`config.general.data_transmission_on`);
          form.unregister(`config.general.data_transmission_off`);
          form.unregister(`config.general.keep_alive`);
          form.unregister(`config.specific`);
          break;
        case DeviceModel.DM_BWS_NB2:
          form.unregister(`config.specific`);
          break;
        case DeviceModel.DM_E3_PLUS_4G:
          form.unregister(`config.specific`);
          break;
        case DeviceModel.DM_E3_PLUS:
          form.unregister(`config.specific`);
          break;
        case DeviceModel.DM_BWS_LL_NB:
        case DeviceModel.DM_BWS_LL_NB_LORA:
        case DeviceModel.DM_BWS_BAIT_NB:
        case DeviceModel.DM_BWS_BAIT_NB_LORA:
          // Intervalos do BWS LL NB ficam no específico (minutos); não há keep alive.
          form.unregister(`config.specific`);
          form.unregister(`config.general.data_transmission_on`);
          form.unregister(`config.general.data_transmission_off`);
          form.unregister(`config.general.keep_alive`);
          break;
        default:
          break;
      }

      form.setValue(
        'config.specific.technology_system_name',
        selected.name.system as TechnologySystemName,
      );
      form.setValue('technology_system_name', selected.name.system as TechnologySystemName);
    }
  };

  const handleChangeClient = (clientName: string) => {
    setName((prev) => {
      const state = { ...prev, document: clientName };
      form.setValue('name', formatConfigurationProfileName(state));
      return state;
    });
  };

  const technology_id = form.watch('technology_id');

  const technology = useMemo(() => {
    return technologies.find((el) => el.id === technology_id);
  }, [technologies, technology_id]);

  useEffect(() => {
    if (defaultValues) {
      const profile = defaultValues.configurationProfile;

      if (profile) {
        form.reset({
          id: isCopy ? undefined : profile.id,
          name: isCopy ? '' : profile.name,
          type: isCopy ? undefined : (profile.type as ConfigurationProfileSchema['type']),
          technology_id: profile.technology_id,
          technology_system_name: profile.technology_system_name as TechnologySystemName,
          client_id: profile.client_id,
          manual_client: profile.manual_client,
          manual_client_register: isCopy ? false : profile.manual_client_register,
          config: profile.config,
        });
      }

      if (defaultValues.technology) {
        form.setValue(
          'config.specific.technology_system_name',
          defaultValues.technology.name.system as TechnologySystemName,
        );
        form.setValue(
          'technology_system_name',
          defaultValues.technology.name.system as TechnologySystemName,
        );
        if (isCopy) {
          setName({ technology: defaultValues.technology.name.brand });
          form.setValue('name', '');
        } else {
          handleChangeName({
            document: profile?.type
              ? (defaultValues.client?.document?.value ?? profile?.manual_client ?? '')
              : '',
            technology: defaultValues.technology.name.brand,
            type: profile?.type,
          });
        }
      }
    }
  }, [defaultValues, form, handleChangeName, isCopy]);

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  return {
    formError,
    clearFormError,
    form,
    register: form.register,
    handleSubmit,
    handleChangeName,
    technology,
    handleChangeTechnology,
    handleChangeClient,
    createMutation,
    updateMutation,
    isEdit: !!isEdit,
    status,
    isActive,
    isInactive,
    isAwaitingValidation,
    isLocked,
    isSubmitting,
  };
}
